/**
 * Service layer — Time Cards (staff) + Time Reviewer (manager).
 *
 * SINGLE data seam for the migrated time tools. The `BACKEND` flag is the cutover switch:
 *   'supabase' — entries live in Supabase (public.time_entries) via api/time-entries.js  ← current
 *   'airtable' — legacy: entries in the Airtable staging table via /api/airtable          ← instant revert
 * Flip the one constant below to revert. Reference data (Members / Phases / Activities / Settings)
 * always reads from Airtable for now; only the entries moved.
 *
 * Both backends return the SAME record shape ({ id, fields: {...Airtable field names...} }) so the
 * two React components (faithful Softr ports) are untouched by the cutover.
 */

import { apiGet, apiGetAll, apiPatch, apiDelete } from './_api.js'
import { getCrewIndex } from './flights.js'
import { computeLiveFinancials, getPhaseExpendedTotals } from './phaseFinancials.js'

// ── CUTOVER SWITCH ────────────────────────────────────────────────────────────
const BACKEND = 'supabase'   // 'supabase' (live) | 'airtable' (instant revert)
const SB = BACKEND === 'supabase'

// ── Airtable tables (legacy path + reference data) ──────────────────────────────
const TBL_STAGING    = 'tblksH7rbKDI4gWeZ'
const TBL_MEMBERS    = 'tblzax2FPEul41fjN'
const TBL_PHASES     = 'tblNl3JpfIEWk9tPX'
const TBL_SETTINGS   = 'tblW5HYfNaJMUuAQX'
const TBL_ACTIVITIES = 'tblobOThkUYBHUfkA'

// ── Authed calls to our server endpoints (Clerk bearer; no browser secret) ──────
async function authToken() { return window.__clerkGetToken ? await window.__clerkGetToken() : null }
async function authedPost(path, body) {
  const token = await authToken()
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body || {}),
  })
  let data = {}; try { data = await res.json() } catch (e) { data = {} }
  if (!res.ok && !data.error) data.error = 'Request failed: ' + res.status
  return data
}
async function authedGet(qs) {
  const token = await authToken()
  const res = await fetch('/api/time-entries?' + qs, { headers: token ? { Authorization: 'Bearer ' + token } : {} })
  let data = {}; try { data = await res.json() } catch (e) { data = {} }
  if (!res.ok) throw new Error(data.error || ('Request failed: ' + res.status))
  return data.rows || []
}

// Supabase row → the Airtable-shaped record the components read. id = the Supabase row id (writes
// target it). Lookup-style fields are wrapped in arrays to match the original Airtable shapes.
function rowToRecord(r) {
  const arr = (v) => (v != null && v !== '' ? [v] : [])
  return {
    id: r.id,
    fields: {
      'Entry Date': r.entry_date,
      'Actual Hours': r.actual_hours,
      // null = "follow Actual Hours" (no reviewer override yet) — BQE Core's clientHours, see
      // docs/TIME_RECORDS.md. Never coerce this to a number here; the UI needs to tell "no override"
      // apart from "override happens to equal Actual Hours".
      'Client Hours': r.client_hours != null ? Number(r.client_hours) : null,
      'Billable': r.billable === true,
      'Memo Draft': r.memo || '',
      'Status': r.status,
      'Confidence': r.confidence || '',
      'Rejection Notes': r.rejection_notes || '',
      'Review Notes': r.review_notes || '',
      'Submitted At': r.submitted_at,
      'Record ID (from Submitter)': arr(r.submitter_member_id),
      'Submitter': arr(r.submitter_member_id),
      'Member Name (from Submitter)': arr(r.member_name),
      'Member Name': r.member_name || '',
      'Project Name': arr(r.project_name),
      'Phase Name': arr(r.phase_name),
      'Phase Airtable ID': arr(r.phase_airtable_id),
      'Activity Code': arr(r.activity_code_id),
      'Activity Code String': arr(r.activity_code_string),
      'Activity UUID': arr(r.activity_uuid),
      'BQE Core Phase UUID': arr(r.bqe_core_phase_uuid),
      'Submitter Resource ID': arr(r.submitter_resource_id),
      'Project Record UID': arr(r.project_record_uid),
      'Bill Rate': r.bill_rate,
      'Charge Amount': r.charge_amount,
      'Post Error': r.post_error || '',
      'Core Entry ID': r.core_entry_id || '',
      // The Core lock signals (docs/TIME_RECORDS.md) — read by both tools to render a row read-only.
      'Sent To Core': r.sent_to_core === true,
      'Date Sent To Core': r.date_sent_to_core || '',
      'Needs Correction': r.needs_correction === true,
      'Correction Note': r.correction_note || '',
      // Entity attribution (Supabase-only): the work item this entry advanced, set by the AI parser.
      'Entity Type': r.entity_type || '',
      'Entity Id': r.entity_id || '',
      'Entity Name': r.entity_name || '',
      'Hold Until': r.hold_until || '',
      'Hold Kind': r.hold_kind || '',
      'Hold Ref Name': r.hold_ref_name || '',
    },
  }
}

// ── Reads ───────────────────────────────────────────────────────────────────────

/** Staff: all entries for one submitter. */
export function getMyTimeRecords(submitterId) {
  if (!submitterId) return Promise.resolve([])
  if (SB) return authedGet('action=mine&memberId=' + encodeURIComponent(submitterId)).then(rows => rows.map(rowToRecord))
  const formula = "FIND('" + submitterId + "', ARRAYJOIN({Record ID (from Submitter)}, ',')) > 0"
  return apiGetAll(TBL_STAGING, 'filterByFormula=' + encodeURIComponent(formula) + '&pageSize=100')
}

/** Reviewer: entries in a date range (the manager week/range view). */
export function getReviewRecords(from, to) {
  if (SB) return authedGet('action=review&from=' + encodeURIComponent(from || '') + '&to=' + encodeURIComponent(to || '')).then(rows => rows.map(rowToRecord))
  const formula = "AND({Entry Date}>='" + from + "',{Entry Date}<='" + to + "')"
  return apiGetAll(TBL_STAGING, 'filterByFormula=' + encodeURIComponent(formula) + '&pageSize=100')
}

/** Reviewer: everything needing attention (Pending Review / Submitted / Rejected / Post Failed). */
export function getNeedsAttention() {
  if (SB) return authedGet('action=needsAttention').then(rows => rows.map(rowToRecord))
  const formula = "OR({Status}='Pending Review',{Status}='Submitted',{Status}='Rejected',{Status}='Post Failed')"
  return apiGetAll(TBL_STAGING, 'filterByFormula=' + encodeURIComponent(formula) + '&pageSize=100')
}

/** Reviewable team members — always Airtable (members not migrated). RAW {id, fields}. */
export function getReviewMembers() {
  const formula = "OR({Member Type}='Team - Principal',{Member Type}='Team - Manager',{Member Type}='Team - Approved')"
  return apiGet(TBL_MEMBERS, 'pageSize=100&filterByFormula=' + encodeURIComponent(formula) +
    '&fields%5B%5D=Member%20Name&fields%5B%5D=Member%20Type')
}

/** Active activity codes — always Airtable. */
export function getActivities() {
  const qs = 'filterByFormula=' + encodeURIComponent('{Active}=1') +
    '&fields%5B%5D=Activity%20Code&fields%5B%5D=Activity%20UUID&pageSize=100'
  return apiGet(TBL_ACTIVITIES, qs).then(records => (records || [])
    .filter(r => r.fields['Activity Code'])
    .map(r => ({ id: r.id, label: r.fields['Activity Code'], uuid: r.fields['Activity UUID'] || '' }))
    .sort((a, b) => a.label.localeCompare(b.label)))
}

/** Phases shown in time-entry submission — reference data (name/budget/type) from Airtable, but
 *  Fee/Hours Expended (and everything derived from them) are corrected against live Supabase time
 *  entries — the Airtable rollup they used to read is frozen since the entries cutover, see
 *  services/phaseFinancials.js. */
export function getEntryPhases() {
  const qs = 'pageSize=100&filterByFormula=' + encodeURIComponent("{Show in Time Entry Submission}='True'") +
    '&fields%5B%5D=Lookup%20Name&fields%5B%5D=Fee%20Expended&fields%5B%5D=Billed%20Fee' +
    '&fields%5B%5D=Contracted%20Fee' +
    '&fields%5B%5D=Hours%20Budget&fields%5B%5D=Hours%20Remaining&fields%5B%5D=Hours%20Expended' +
    '&fields%5B%5D=Phase%20Work%20Remaining&fields%5B%5D=Contract%20Type&fields%5B%5D=Effective%20Billing%20Rate' +
    '&fields%5B%5D=Fee%20Expended%20Starting%20Point&fields%5B%5D=Hours%20Expended%20Starting%20Point'
  return apiGet(TBL_PHASES, qs).then(async (records) => {
    const list = records || []
    let totals = {}
    try { totals = await getPhaseExpendedTotals(list.map(r => r.id)) }
    catch (e) { console.error('[timecards] live phase totals failed', e && e.message) }
    return list.map(r => {
      const pf = r.fields
      const num = (v) => v != null && isFinite(Number(v)) ? Number(v) : null
      const r1 = (v) => { const n = num(v); return n != null ? Math.round(n * 10) / 10 : null }
      // NOTE (2026-08 fix): this used to return `contractedFee: billedFee` — a mislabeling that made
      // every "is this Fixed-Fee phase over budget" check in TimeReviewModal.jsx (isOverBudget, the
      // fee-remaining/percent-used math) compare Fee Expended against how much has been INVOICED so
      // far instead of the real contract ceiling. A phase 10% billed but 40% worked read as blown
      // way over budget when it wasn't. `billedFee` and `contractedFee` are now two distinct fields —
      // see docs/EBR_FRAMEWORK.md.
      const billedFee = pf['Billed Fee'] != null ? Number(pf['Billed Fee']) : 0
      const contractedFee = pf['Contracted Fee'] != null ? Number(pf['Contracted Fee']) : 0
      const hoursBudget = r1(pf['Hours Budget'])
      const contractType = pf['Contract Type'] || null
      const t = totals[r.id] || { hours: 0, fee: 0, recentHours: 0, pendingHours: 0, pendingFee: 0, firstPostedDate: null, lastPostedDate: null }
      const live = computeLiveFinancials({
        billedFee, contractedFee, hoursBudget,
        feeStartingPoint: pf['Fee Expended Starting Point'],
        hoursStartingPoint: pf['Hours Expended Starting Point'],
        supabaseFee: t.fee, supabaseHours: t.hours,
        contractType, recentHours: t.recentHours, pendingHours: t.pendingHours, pendingFee: t.pendingFee,
        firstPostedDate: t.firstPostedDate, lastPostedDate: t.lastPostedDate,
      })
      return {
        id: r.id,
        name: (Array.isArray(pf['Lookup Name']) ? pf['Lookup Name'][0] : pf['Lookup Name']) || '',
        feeExpended: Math.round(live.feeExpended * 100) / 100,
        billedFee,
        contractedFee,
        hoursBudget,
        hoursRemaining: live.hoursRemaining != null ? Math.round(live.hoursRemaining * 10) / 10 : null,
        hoursExpended: Math.round(live.hoursExpended * 10) / 10,
        phaseWorkRemaining: num(pf['Phase Work Remaining']),
        contractType,
        effectiveBillingRate: live.effectiveBillingRate,
        projectedBudgetDate: live.projectedBudgetDate,
        pendingHours: Math.round(live.pendingHours * 10) / 10,
        pendingFee: Math.round(live.pendingFee * 100) / 100,
        feeExpendedInclPending: Math.round(live.feeExpendedInclPending * 100) / 100,
      }
    }).filter(p => p.name).sort((a, b) => a.name.localeCompare(b.name))
  })
}

/** Firm Target EBR — always Airtable. */
export function getTargetEbr() {
  return apiGet(TBL_SETTINGS, 'pageSize=1&fields%5B%5D=Target%20EBR').then(records => {
    const v = records && records[0] && records[0].fields['Target EBR']
    const n = Number(v)
    return (v != null && isFinite(n) && n > 0) ? n : 125
  }).catch(() => 125)
}

// ── Writes ────────────────────────────────────────────────────────────────────

export function saveEntryEdit(id, { hours, memo, billable, activityId, phaseId, entityType, entityId, entityName }) {
  if (SB) return authedPost('/api/time-entries', { action: 'saveEdit', id, hours, memo, billable, activityId, phaseId, entityType, entityId, entityName }).then(d => d.row || d)
  const fields = { 'Actual Hours': hours, 'Memo Draft': memo, 'Billable': billable }
  if (activityId) fields['Activity Code'] = [activityId]
  fields['Phase Airtable ID'] = phaseId ? [phaseId] : []
  return apiPatch(TBL_STAGING, id, fields)   // entity attribution is Supabase-only — not written to Airtable
}

/**
 * A submitter's live work items, for the reviewer's entity-attribution dropdown. Maps the unified
 * RASCI inbox to compact { entity_type, entity_id, entity_name, project_name } — same shape the intake
 * parser uses. Deduped by entity; non-entity rows (Opportunity/Lead/etc.) dropped.
 */
const ENTITY_ATTR_TYPES = {
  'Drawing Sheet': 1, 'BIM System': 1, 'Thread': 1, 'Deliverable': 1,
  'Agency Review': 1, 'Opportunity': 1, 'Lead': 1, 'Billing Run': 1,
}
// Some per-entity project lookups (e.g. Project (from Drawing Sheet)) return the project RECORD ID, not
// its name. Accept a value only if it's a readable name, never a recXXX id.
const readableName = v => { const s = String(v || '').trim(); return (s && !/^rec[A-Za-z0-9]{14,}$/.test(s)) ? s : '' }

// Firm-overhead work units are not project-scoped — they belong to an overhead category instead.
const OVERHEAD_TYPE_CATEGORY = { 'Opportunity': 'bd', 'Lead': 'bd', 'Billing Run': 'ops' }
const normScope = v => { const s = Array.isArray(v) ? v[0] : v; return String(s || '').replace(/\*\*/g, '').trim().toLowerCase() }
function phaseScope(projectName, phaseName) {
  // Parse the project out of the phase Lookup Name ("[YY-##]-[CODE] - [Project] - [Phase]") — the
  // reliable signal. The entry's denormalized Project Name can be the phase string or empty, so it's
  // only a fallback. Split on " - "/" – " (the code's internal hyphens have no surrounding spaces).
  let proj = ''
  const raw = Array.isArray(phaseName) ? phaseName[0] : phaseName
  if (raw) { const parts = String(raw).split(/\s[–-]\s/); if (parts.length >= 3) proj = normScope(parts.slice(1, -1).join(' - ')) }
  if (!proj) proj = normScope(projectName)
  if (proj && !/alder james overhead/.test(proj)) return { kind: 'project', key: proj }
  const pn = normScope(phaseName)
  if (/business development|biz dev|\bbd\b|marketing|proposal/.test(pn)) return { kind: 'bd' }
  if (/operation|invoic|billing|finance|bookkeep|account/.test(pn)) return { kind: 'ops' }
  return { kind: 'none' }
}
/**
 * Scope work items to a time entry's phase/project (shared by the staff card + reviewer pickers).
 * Project phases show that project's work (milestones/sheets/threads/BIM/agency reviews); overhead
 * phases show their category's work units (Business Development → Opportunity/Lead, Operations →
 * Billing Run). Returns [] for an unrecognized/no-project context.
 */
export function scopeWorkItemsForEntry(items, opts = {}) {
  const scope = phaseScope(opts.projectName, opts.phaseName)
  return (items || []).filter(it => {
    const cat = OVERHEAD_TYPE_CATEGORY[it.entity_type]
    if (scope.kind === 'project') return !cat && normScope(it.project_name) === scope.key
    if (scope.kind === 'bd' || scope.kind === 'ops') return cat === scope.kind
    return false
  })
}
/** A project's open milestones for the time card's milestone picker (the entry's phase first). */
export function getProjectMilestones(projectUid, phaseId) {
  if (!projectUid) return Promise.resolve([])
  return authedGet('action=milestones&projectUid=' + encodeURIComponent(projectUid) + '&phaseId=' + encodeURIComponent(phaseId || ''))
    .then(rows => rows || []).catch(() => [])
}
/** Project time (not overhead) needs a milestone before it can be submitted (api/time-entries.js). */
export function entryNeedsMilestone(fields) {
  const one = (v) => Array.isArray(v) ? v[0] : v
  const uid = one(fields && fields['Project Record UID']), proj = String(one(fields && fields['Project Name']) || '')
  if (!uid || /overhead/i.test(proj)) return false
  const t = one(fields['Entity Type'])
  return t !== 'Deliverable' && t !== 'Thread'
}
export async function getSubmitterWorkItems(memberId) {
  if (!memberId) return []
  // The person's work IS their crewed flights (RASCI retired 2026-09-02 — docs/FLOW_RECONCILIATION_PLAN.md):
  // each milestone flight is a Deliverable candidate, each thread flight a Thread, and a squadron
  // contributes one Thread per member thread. The modal still scopes the result to the entry's
  // phase/project via scopeWorkItemsForEntry.
  let idx
  try { idx = await getCrewIndex() } catch { return [] }
  const seen = {}, out = []
  const push = (type, id, name, project) => {
    if (!ENTITY_ATTR_TYPES[type] || !id || seen[id]) return
    seen[id] = 1
    out.push({ entity_type: type, entity_id: id, entity_name: name || id, project_name: readableName(project) })
  }
  ;(idx.flights || []).forEach(f => {
    if (!(f.crew || []).some(c => c.memberAirtableId === memberId)) return
    if (f.kind === 'thread') push('Thread', f.threadId, f.milestoneName || f.flightNumber, f.projectName)
    else if (f.kind === 'squadron') (f.threads || []).forEach(t => push('Thread', t.threadId, t.title, f.projectName))
    else push('Deliverable', f.milestoneId, f.milestoneName, f.projectName)
  })
  return out
}

// Accepting a suggested time entry (docs/ACTIVITY_HUD_PLAN.md step 2): creates a Pending Review
// entry for the signed-in person directly — no narrative parser. The server picks the submitter.
export function createTimeEntry({ date, hours, phaseId, activityCode, memo, reviewNotes, source }) {
  return authedPost('/api/time-entries', { action: 'create', date, hours, phaseId, activityCode, memo, reviewNotes, source }).then(d => d.row || d)
}

// The activity-code rule (Matt, 2026-10-15): Alder James Overhead (00-01) takes only overhead codes
// (OP-, or PTO- for time off); every other project takes only AS- codes. Core rejects anything else,
// so the app refuses to submit or approve it. Mirrored server-side in api/time-entries.js.
export function activityCodeProblem(phaseName, code) {
  const ph = String((Array.isArray(phaseName) ? phaseName[0] : phaseName) || '').trim()
  const c = String((Array.isArray(code) ? code[0] : code) || '').trim()
  if (!ph) return 'No phase. Time for a project that is not set up yet goes to Business Development.'
  if (!c) return null
  if (/^00-01(?![0-9])/.test(ph)) return /^(OP-|PTO-)/.test(c) ? null : c + ' is a project code. Alder James Overhead (00-01) takes OP- or PTO- codes.'
  return /^AS-/.test(c) ? null : c + ' is an overhead code. Project time takes an AS- code.'
}

export function setEntryStatus(id, status, extra) {
  if (SB) return authedPost('/api/time-entries', { action: 'setStatus', id, status, submitted_at: (extra && extra['Submitted At']) || undefined }).then(d => { if (d && d.error) throw new Error(d.error); return d.row || d })
  return apiPatch(TBL_STAGING, id, Object.assign({ 'Status': status }, extra || {}))
}

export function rejectEntry(id, note) {
  if (SB) return authedPost('/api/time-entries', { action: 'reject', id, note }).then(d => d.row || d)
  return apiPatch(TBL_STAGING, id, { 'Status': 'Rejected', 'Rejection Notes': note || '' })
}

/** Reviewer: hold a submitted entry for a project being onboarded (database/308). kind: 'lead'|'opportunity'. */
export function holdEntry(id, { kind, refId, refName }) {
  return authedPost('/api/time-entries', { action: 'hold', id, kind, refId, refName })
}
export function releaseHold(id) {
  return authedPost('/api/time-entries', { action: 'releaseHold', id })
}

export function approveEntry(id) {
  if (SB) return authedPost('/api/time-entries', { action: 'approve', id }).then(d => { if (d && d.error) throw new Error(d.error); return d.row || d })
  return apiPatch(TBL_STAGING, id, { 'Status': 'Approved' })
}

export function deleteEntry(id) {
  if (SB) return authedPost('/api/time-entries', { action: 'delete', id }).then(d => d.ok)
  return apiDelete(TBL_STAGING, id)
}

/** Generic patch used by the reviewer's atPatch shim + the timecard bulk-submit. In Supabase mode the
 *  Airtable-field-named patch is translated to api/time-entries.js calls (with denorm for phase/activity). */
export async function patchEntry(id, fields) {
  if (!SB) {
    // Legacy Airtable revert path: entity attribution is Supabase-only — never PATCH it to Airtable.
    const ff = { ...(fields || {}) }; delete ff['Entity Type']; delete ff['Entity Id']; delete ff['Entity Name']
    return apiPatch(TBL_STAGING, id, ff)
  }
  const f = fields || {}
  const isEdit = ['Actual Hours', 'Client Hours', 'Memo Draft', 'Billable', 'Activity Code', 'Phase Airtable ID', 'Entity Type', 'Entity Id', 'Entity Name'].some(k => k in f)
  let last
  if (isEdit) {
    const body = { action: 'saveEdit', id }
    if ('Actual Hours' in f) body.hours = f['Actual Hours']
    // null clears any override and reverts to following Actual Hours — a legitimate value, not "absent".
    if ('Client Hours' in f) body.clientHours = f['Client Hours']
    if ('Memo Draft' in f) body.memo = f['Memo Draft']
    if ('Billable' in f) body.billable = f['Billable']
    if ('Activity Code' in f) body.activityId = Array.isArray(f['Activity Code']) ? f['Activity Code'][0] : f['Activity Code']
    if ('Phase Airtable ID' in f) body.phaseId = Array.isArray(f['Phase Airtable ID']) ? f['Phase Airtable ID'][0] : f['Phase Airtable ID']
    if ('Entity Type' in f) body.entityType = f['Entity Type'] || ''
    if ('Entity Id' in f) body.entityId = f['Entity Id'] || ''
    if ('Entity Name' in f) body.entityName = f['Entity Name'] || ''
    last = await authedPost('/api/time-entries', body)
    if ('Status' in f) last = await authedPost('/api/time-entries', { action: 'setStatus', id, status: f['Status'] })
  } else if (f['Status'] === 'Rejected' && 'Rejection Notes' in f) {
    last = await authedPost('/api/time-entries', { action: 'reject', id, note: f['Rejection Notes'] })
  } else if ('Status' in f) {
    last = await authedPost('/api/time-entries', { action: 'setStatus', id, status: f['Status'], submitted_at: f['Submitted At'] || undefined })
  } else {
    last = {}
  }
  // A refused status change (e.g. an activity code that doesn't fit the project) must reach the
  // caller's catch path instead of looking like success.
  if (last && last.error && 'Status' in f) throw new Error(last.error)
  return (last && last.row) || last
}

/**
 * Live check: has BQE Core attached this entry to an invoice yet? See docs/TIME_RECORDS.md's
 * "Revising a Posted entry" section — `invoiceId` is the one fact Core carries that actually
 * answers this; sent_to_core/core_entry_id only prove the entry reached Core at some point.
 * Airtable-mode entries never reach BQE Core through this app's lock, so this is Supabase-only.
 */
export async function getCoreBillStatus(id) {
  if (!SB) return { inCore: false, billed: false }
  const token = await authToken()
  const res = await fetch('/api/time-entries?action=coreBillStatus&id=' + encodeURIComponent(id), {
    headers: token ? { Authorization: 'Bearer ' + token } : {},
  })
  let data = {}; try { data = await res.json() } catch (e) { data = {} }
  if (!res.ok) throw new Error(data.error || ('Request failed: ' + res.status))
  return data
}

/**
 * Revise an already-Posted entry — the one path that can still change hours/memo/billable/phase/
 * activity/entity-attribution on an in-Core row, and only while Core still shows it unbilled (the
 * server re-checks live on every call, never trusting a stale client-side read). Same
 * Airtable-field-named patch shape as patchEntry's edit branch, routed to revisePosted instead of
 * saveEdit — the Core lock refuses saveEdit outright on a Posted row, on purpose.
 */
export async function reviseEntry(id, fields) {
  if (!SB) return { error: 'Revise is only available on Supabase-backed entries.' }
  const f = fields || {}
  const body = { action: 'revisePosted', id }
  if ('Actual Hours' in f) body.hours = f['Actual Hours']
  if ('Client Hours' in f) body.clientHours = f['Client Hours']
  if ('Memo Draft' in f) body.memo = f['Memo Draft']
  if ('Billable' in f) body.billable = f['Billable']
  if ('Activity Code' in f) body.activityId = Array.isArray(f['Activity Code']) ? f['Activity Code'][0] : f['Activity Code']
  if ('Phase Airtable ID' in f) body.phaseId = Array.isArray(f['Phase Airtable ID']) ? f['Phase Airtable ID'][0] : f['Phase Airtable ID']
  if ('Entity Type' in f) body.entityType = f['Entity Type'] || ''
  if ('Entity Id' in f) body.entityId = f['Entity Id'] || ''
  if ('Entity Name' in f) body.entityName = f['Entity Name'] || ''
  const res = await authedPost('/api/time-entries', body)
  return (res && res.row) || res
}

// ── Server endpoints (intake / BQE post / rejection notify) ──────────────────────

export function submitIntake(date, narrative, submitterRecordId) {
  return authedPost('/api/parse-intake', { date, narrative, submitterRecordId, target: BACKEND })
}

export function postTimeEntryToCore(id) {
  return authedPost('/api/post-time-entry', SB ? { timeEntryId: id } : { stagingRecordId: id })
}

export function notifyRejection(id, note) {
  return authedPost('/api/notify-rejection', SB ? { timeEntryId: id, note } : { stagingRecordId: id, note })
}

/** Manual "run now" for the nightly Billed Fee ↔ BQE Core sync (api/sync-billed-fee.js). Gated
 *  server-side to 'admin'/'timecard-admin'/'billing-admin'. Returns { scannedPhases, invoiceCount,
 *  updated, lastRunAt } or throws with the server's error message. */
export function syncBilledFee() {
  return authedPost('/api/sync-billed-fee', {}).then(d => {
    if (d && d.error) throw new Error(d.error)
    return d
  })
}

/** Cheap read-only check (no BQE call) — when did the Billed Fee sync last complete (nightly or
 *  manual), so the UI can show it before anyone decides whether to run it again. Returns the ISO
 *  timestamp string, or null if it has never run. */
export function getBilledFeeSyncStatus() {
  return authToken().then(token =>
    fetch('/api/sync-billed-fee?status=1', { headers: token ? { Authorization: 'Bearer ' + token } : {} })
      .then(res => res.json().then(d => {
        if (!res.ok) throw new Error(d.error || ('Request failed: ' + res.status))
        return d.lastRunAt || null
      }))
  )
}
